import { SellPaymentMethod } from "../sell/sell.entity";
import type { Sell } from "../sell/sell.entity";
import type { CashSession } from "./cash-session.entity";
import type { CashMovement } from "./cash-movement.entity";
import { paymentMethodLabel } from "../sell/utils/sales-analytics";

export type PaymentBreakdownRow = {
  method: SellPaymentMethod;
  label: string;
  count: number;
  total: number;
};

export type CashRegisterSummary = {
  sessionId: string;
  dayKey: string;
  openingCash: number;
  closingCash: number | null;
  totalSales: number;
  /** Vendas em aberto (rascunho) vinculadas à sessão */
  countOpenSales: number;
  countCompletedSales: number;
  /** Total de vendas (abertas + concluídas) na sessão */
  totalSellsInSession: number;
  breakdown: PaymentBreakdownRow[];
  totalCashSales: number;
  /** Soma das entradas manuais de caixa. */
  totalEntries: number;
  /** Soma das retiradas manuais de caixa. */
  totalWithdrawals: number;
  /** Abertura + entradas − retiradas + vendas em dinheiro. */
  expectedCashApprox: number;
  /** Informado no fechamento − esperado (se houver fechamento). */
  differenceApprox: number | null;
};

export function buildCashRegisterSummary(
  session: CashSession,
  sells: Sell[],
  movements: CashMovement[] = []
): CashRegisterSummary {
  const linked = sells.filter((s) => s.getCashSessionId() === session.id);

  const openInSession = linked.filter(
    (s) => s.getPaymentMethod() === SellPaymentMethod.UNPAID
  );

  const completed = linked.filter(
    (s) =>
      s.getCompletedAt() &&
      s.getPaymentMethod() !== SellPaymentMethod.UNPAID
  );

  const acc = new Map<
    SellPaymentMethod,
    { count: number; total: number }
  >();
  for (const m of Object.values(SellPaymentMethod)) {
    acc.set(m, { count: 0, total: 0 });
  }

  let totalSales = 0;
  for (const s of completed) {
    const t = s.getSellTotal();
    totalSales += t;
    const pm = s.getPaymentMethod();
    const cur = acc.get(pm)!;
    cur.count += 1;
    cur.total += t;
  }

  const breakdown: PaymentBreakdownRow[] = [];
  for (const method of Object.values(SellPaymentMethod) as SellPaymentMethod[]) {
    const v = acc.get(method)!;
    if (v.count === 0 && method === SellPaymentMethod.UNPAID) continue;
    breakdown.push({
      method,
      label: paymentMethodLabel[method],
      count: v.count,
      total: v.total,
    });
  }
  breakdown.sort((a, b) => b.total - a.total);

  const totalCashSales = acc.get(SellPaymentMethod.MONEY)?.total ?? 0;

  const totalEntries = movements
    .filter((m) => m.getType() === "entry")
    .reduce((sum, m) => sum + m.getAmount(), 0);

  const totalWithdrawals = movements
    .filter((m) => m.getType() === "withdrawal")
    .reduce((sum, m) => sum + m.getAmount(), 0);

  const opening = session.getOpeningCash();
  const expected = opening + totalEntries - totalWithdrawals + totalCashSales;
  const closing = session.getClosingCash();
  const differenceApprox =
    closing !== null && closing !== undefined ? closing - expected : null;

  return {
    sessionId: session.id,
    dayKey: session.getDayKey(),
    openingCash: opening,
    closingCash: closing,
    totalSales,
    countOpenSales: openInSession.length,
    countCompletedSales: completed.length,
    totalSellsInSession: linked.length,
    breakdown,
    totalCashSales,
    totalEntries,
    totalWithdrawals,
    expectedCashApprox: expected,
    differenceApprox,
  };
}

export function cashHealthHint(diff: number | null): {
  label: string;
  severity: "success" | "warning" | "info";
} {
  if (diff === null) {
    return { label: "Informe o valor em dinheiro no fechamento para conferir.", severity: "info" };
  }
  const ad = Math.abs(diff);
  if (ad < 0.02) {
    return { label: "Conferência: valores alinhados.", severity: "success" };
  }
  if (ad < 10) {
    return {
      label: "Pequena diferença — pode ser arredondamento ou troco não contabilizado.",
      severity: "warning",
    };
  }
  return {
    label: "Diferença relevante — revise vendas em dinheiro e o valor contado.",
    severity: "warning",
  };
}
